//@ts-nocheck
import React from "react";
import { motion } from "framer-motion";

// ─────────────────────────────────────────────────────────────────────────────
// ASTRIA JAPAN TALK — Companion card renderer
// Renders the AI's streamed text response for the "Astria Japan Talk"
// category's conversational Companion subcategories (Relationship / Comfort
// / Healing / Daily Companion / Love Mode JP) inside a minimal, clean,
// pastel, airy card — per client's JP UI Style Guide:
//   Container: background #FFFFFF, padding 32, radius 16,
//              shadow 0 8px 24px rgba(0,0,0,0.06), maxWidth 720
//   Section block: padding 24, radius 12, background #FAFAFA
//   Typography: title 22-24px/weight 500/#222, body 15px/line-height 1.6-1.7/#444
//
// Note: "Kyusei Viral JP" and "Timing Flow JP" are NOT chat subcategories —
// the sidebar redirects those to standalone dashboard screens
// (KyuseiJapanResult.tsx / JPTimingFlowResult.tsx) that fetch deterministic
// content from /api/backend/astria-japan-kyusei on tab-open, per the
// client's "Morning Check-in" flow (no typing required). Only the Companion
// modes are a real conversation, so this card only needs to render that.
//
// Scope: "Astria Japan Talk" ONLY. Does not touch "Astria Japan" (Big3/
// Signs/Personality/Compatibility/Daily Flow/Quiet Letter) or any other
// country's chat UI — purely additive renderer swapped in by
// ChatInterface.tsx only when category name === "Astria Japan Talk".
// ─────────────────────────────────────────────────────────────────────────────

const JP_FONT = "'Noto Sans JP', sans-serif";

const COLORS = {
  bg: "#FFFFFF",
  section: "#FAFAFA",
  cardBorder: "#EFEFEF",
  titleColor: "#222222",
  bodyColor: "#444444",
  mutedColor: "#777777",
  shadow: "0 8px 24px rgba(0,0,0,0.06)",
  accent: "#B0BEC5",
};

const MODE_META: Record<string, { headerTitle: string; headerSub: string; emoji: string }> = {
  relationship: { headerTitle: "関係のタイミング", headerSub: "Relationship", emoji: "🤝" },
  comfort: { headerTitle: "そっと、安心を", headerSub: "Comfort", emoji: "🕊️" },
  healing: { headerTitle: "静かな回復", headerSub: "Healing", emoji: "🌿" },
  love: { headerTitle: "やわらかな想い", headerSub: "Love", emoji: "💗" },
  daily: { headerTitle: "インナー・スペース", headerSub: "Daily Companion", emoji: "🌙" },
};

function resolveCompanionMode(subCategoryName?: string | null): string {
  if (!subCategoryName) return "daily";
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("relationship")) return "relationship";
  if (lower.includes("comfort")) return "comfort";
  if (lower.includes("healing")) return "healing";
  if (lower.includes("love")) return "love";
  return "daily";
}

interface AstriaJapanTalkCardProps {
  content: string;
  subCategoryName?: string | null;
  isStreaming?: boolean;
}

const AstriaJapanTalkCard: React.FC<AstriaJapanTalkCardProps> = ({
  content,
  subCategoryName,
  isStreaming,
}) => {
  const mode = resolveCompanionMode(subCategoryName);
  const meta = MODE_META[mode];
  const body = content || "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      style={{
        background: COLORS.bg,
        borderRadius: 16,
        boxShadow: COLORS.shadow,
        maxWidth: 720,
        width: "100%",
        margin: "0 auto",
        padding: 28,
        fontFamily: JP_FONT,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: COLORS.section,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {meta.emoji}
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 500, color: COLORS.titleColor, lineHeight: 1.3 }}>
            {meta.headerTitle}
          </div>
          <div style={{ fontSize: 13, color: COLORS.mutedColor, marginTop: 2 }}>{meta.headerSub}</div>
        </div>
      </div>

      <div
        style={{
          background: COLORS.section,
          borderRadius: 12,
          padding: 24,
          fontSize: 15,
          lineHeight: 1.7,
          color: COLORS.bodyColor,
          whiteSpace: "pre-wrap",
        }}
      >
        {body}
      </div>

      {isStreaming && (
        <div style={{ display: "flex", gap: 4, marginTop: 16, paddingLeft: 4 }}>
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: COLORS.accent,
                display: "inline-block",
              }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default AstriaJapanTalkCard;
